import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, Plus, Search, Trash2, Pencil, UserPlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { HrPageShell } from './components/HrPageShell';
import { EmployeeAvatar, StatusBadge, DeleteConfirmDialog, EmptyState } from './components/hr-ui';
import { useEmployees } from './hr-store';
import {
  useDeleteEmployeeMutation,
  useHrDepartmentsQuery,
  useHrEmployeesQuery,
  useHrLevelsQuery,
  useUpsertEmployeeMutation,
} from '@/client/api/hr/people';
import { useCreateAdminUserMutation } from '@/client/api/admin/users.mutation';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { ImportStaffDialog } from './components/ImportStaffDialog';
import { LocationSearchInput } from './components/LocationSearchInput';
import UserSearchSelect from '@/client/components/common/UserSearchSelect';
import type { AdminUserSearchUser } from '@/client/types/admin/users-orgs';
import {
  EMPLOYMENT_STATUS_BADGE,
  EMPLOYMENT_STATUS_LABEL,
  EMPLOYMENT_TYPE_LABEL,
  type Employee,
  type EmploymentStatus,
  type EmploymentType,
} from '@/client/types/hr';

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const STATUS_FILTERS: (EmploymentStatus | 'all')[] = ['all', 'active', 'on_leave', 'resigned', 'terminated'];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

type FormState = {
  name: string;
  email: string;
  title: string;
  departmentId: string;
  levelId: string;
  managerId: string;
  employmentType: EmploymentType;
  status: EmploymentStatus;
  location: string;
  startDate: string;
};

/** A brand-new FeelyFeely account, created with the employee. */
type NewAccount = { firstName: string; lastName: string; email: string; phone: string };
const EMPTY_ACCOUNT: NewAccount = { firstName: '', lastName: '', email: '', phone: '' };

const emptyForm: FormState = {
  name: '',
  email: '',
  title: '',
  departmentId: '',
  levelId: '',
  managerId: '',
  employmentType: 'full_time',
  status: 'active',
  location: '',
  startDate: new Date().toISOString().slice(0, 10),
};

function toForm(e: Employee | null): FormState {
  if (!e) return emptyForm;
  return {
    name: e.name,
    email: e.email,
    title: e.title,
    departmentId: e.departmentId ?? '',
    levelId: e.levelId ?? '',
    managerId: e.managerId ?? '',
    employmentType: e.employmentType,
    status: e.status,
    location: e.location ?? '',
    startDate: e.startDate.slice(0, 10),
  };
}

const DirectoryPage = () => {
  const navigate = useNavigate();
  const employees = useEmployees();
  const employeesQ = useHrEmployeesQuery();
  const upsert = useUpsertEmployeeMutation();
  const createAccount = useCreateAdminUserMutation();
  const departmentsList = useHrDepartmentsQuery().data ?? [];
  const levelsList = useHrLevelsQuery().data ?? [];
  const canCreateAccount = useGate(PERMISSIONS.user.edit).allowed;
  const remove = useDeleteEmployeeMutation();
  const canManage = useGate(PERMISSIONS.hr.manage).allowed;
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<EmploymentStatus | 'all'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleting, setDeleting] = useState<Employee | null>(null);
  // Add only: the FeelyFeely account the new employee is created from.
  const [account, setAccount] = useState<AdminUserSearchUser | null>(null);
  const [accountQuery, setAccountQuery] = useState('');
  // Add only: no account yet, so create one with the employee.
  const [newAccount, setNewAccount] = useState<NewAccount | null>(null);
  // Add only: they have no FeelyFeely account yet; record them now, link later.
  const [pendingAccount, setPendingAccount] = useState(false);

  const departments = departmentsList;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter(
      (e) =>
        (departmentFilter === 'all' || (e.departmentId ?? '') === departmentFilter) &&
        (statusFilter === 'all' || e.status === statusFilter) &&
        (!q || e.name.toLowerCase().includes(q) || e.email.toLowerCase().includes(q) || e.title.toLowerCase().includes(q)),
    );
  }, [employees, search, departmentFilter, statusFilter]);

  const stats = useMemo(
    () => [
      { key: 'total', label: 'Total employees', value: employees.length },
      { key: 'active', label: 'Active', value: employees.filter((e) => e.status === 'active').length },
      { key: 'on_leave', label: 'On leave', value: employees.filter((e) => e.status === 'on_leave').length },
      { key: 'departments', label: 'Departments', value: departments.length },
    ],
    [employees, departments],
  );

  const managerName = (id?: string | null) => employees.find((e) => e.id === id)?.name ?? '—';

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setAccount(null);
    setAccountQuery('');
    setNewAccount(null);
    setPendingAccount(false);
    setDialogOpen(true);
  };

  const openEdit = (e: Employee) => {
    setEditing(e);
    setForm(toForm(e));
    setAccount(null);
    setDialogOpen(true);
  };

  const pickAccount = (u: AdminUserSearchUser) => {
    setAccount(u);
    setAccountQuery('');
    setForm((f) => ({ ...f, name: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || f.name, email: u.email || f.email }));
  };

  const save = (uid?: number) =>
    upsert.mutate(
      {
        id: editing?.id,
        name: form.name.trim(),
        email: form.email.trim(),
        title: form.title.trim(),
        departmentId: form.departmentId || null,
        levelId: form.levelId || null,
        managerId: form.managerId || null,
        employmentType: form.employmentType,
        status: form.status,
        location: form.location.trim() || null,
        startDate: form.startDate,
        ...(uid ? { uid } : {}),
      },
      {
        onSuccess: (saved) => {
          toast.success(editing ? 'Employee updated' : 'Employee added');
          setDialogOpen(false);
          // Console access and staff status are granted on the profile.
          if (!editing && saved?.id) navigate(`/admin/human-resource/people/directory/${saved.id}`);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save the employee'),
      },
    );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (editing) {
      save();
      return;
    }
    if (account) {
      save(account.uid);
      return;
    }
    if (pendingAccount) {
      if (!form.email.trim()) {
        toast.error('Add their email, so their account can be found once they sign up');
        return;
      }
      save();
      return;
    }
    if (!newAccount) {
      toast.error('Pick their FeelyFeely account, create one, or add them as pending');
      return;
    }
    if (!newAccount.firstName.trim() || !newAccount.lastName.trim() || !(newAccount.email.trim() || newAccount.phone.trim())) {
      toast.error('A new account needs a first and last name, and an email or phone');
      return;
    }
    try {
      const created = await createAccount.mutateAsync({
        first_name: newAccount.firstName.trim(),
        last_name: newAccount.lastName.trim(),
        email1: newAccount.email.trim() || undefined,
        phone1: newAccount.phone.trim() || undefined,
      });
      const uid = (created as { data?: { uid?: number } })?.data?.uid ?? (created as { uid?: number })?.uid;
      if (!uid) {
        toast.error('The account was created but its id did not come back. Find it with the search and link it.');
        setNewAccount(null);
        return;
      }
      save(uid);
    } catch (err) {
      toast.error((err as Error).message || 'Could not create the account');
    }
  };

  const confirmDelete = () => {
    if (!deleting) return;
    const target = deleting;
    remove.mutate(target.id, {
      onSuccess: () => toast.success(`${target.name} removed`),
      onError: (err) => toast.error((err as Error).message || 'Could not remove the employee'),
    });
    setDeleting(null);
  };

  return (
    <HrPageShell
      mock={false}
      title="Directory"
      description="The full employee roster — roles, departments, reporting lines, and employment status."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'People' }, { label: 'Directory' }]}
      icon={Users}
      actions={
        <div className="flex flex-wrap gap-2">
          <PermissionButton code={PERMISSIONS.hr.manage} variant="outline" className="gap-2" onClick={() => setImportOpen(true)}>
            <UserPlus className="h-4 w-4" /> Import staff accounts
          </PermissionButton>
          <PermissionButton code={PERMISSIONS.hr.manage} className="gap-2" onClick={openAdd}>
            <Plus className="h-4 w-4" /> Add employee
          </PermissionButton>
        </div>
      }
      stats={stats}
    >
      <div className="mb-4 flex flex-wrap gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, title…"
            className={`${inputClass} w-64 pl-8`}
          />
        </div>
        <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2">
          <option value="all">All departments</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as EmploymentStatus | 'all')} className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2">
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {s === 'all' ? 'All statuses' : EMPLOYMENT_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      {employeesQ.isError ? (
        <EmptyState label={`Could not load employees: ${(employeesQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : employeesQ.isLoading ? (
        <EmptyState label="Loading employees…" />
      ) : filtered.length === 0 ? (
        <EmptyState label={employees.length ? 'No employees match these filters.' : 'No employees yet. Add one, or import staff accounts.'} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Department</th>
                <th className="px-4 py-3 font-medium">Manager</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((e) => (
                <tr key={e.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/admin/human-resource/people/directory/${e.id}`)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <EmployeeAvatar employee={e} />
                      <div className="min-w-0">
                        <p className="flex items-center gap-1.5 truncate font-medium text-gray-900">
                          {e.name}
                          {e.accountPending && (
                            <StatusBadge label="Pending account" className="bg-amber-50 text-amber-800" />
                          )}
                        </p>
                        <p className="truncate text-xs text-gray-400">{e.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{e.title}</td>
                  <td className="px-4 py-3 text-gray-600">{e.department}</td>
                  <td className="px-4 py-3 text-gray-600">{managerName(e.managerId)}</td>
                  <td className="px-4 py-3 text-gray-600">{EMPLOYMENT_TYPE_LABEL[e.employmentType]}</td>
                  <td className="px-4 py-3">
                    <StatusBadge label={EMPLOYMENT_STATUS_LABEL[e.status]} className={EMPLOYMENT_STATUS_BADGE[e.status]} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className={`flex items-center justify-end gap-1 ${canManage ? '' : 'hidden'}`}>
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          openEdit(e);
                        }}
                        className="text-gray-400 hover:text-gray-700"
                        aria-label={`Edit ${e.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setDeleting(e);
                        }}
                        className="text-gray-400 hover:text-red-600"
                        aria-label={`Remove ${e.name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit employee' : 'Add employee'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {!editing &&
              (account ? (
                <div className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="min-w-0 truncate text-sm text-gray-700">
                    Linked to {account.email || `${account.first_name} ${account.last_name}`} · uid {account.uid}
                  </p>
                  <button
                    type="button"
                    onClick={() => setAccount(null)}
                    className="text-gray-400 hover:text-gray-700"
                    aria-label="Remove linked account"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : pendingAccount ? (
                <div className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="text-sm text-amber-800">
                    No FeelyFeely account yet. Add their email below, then link the account from their profile once they sign up.
                  </p>
                  <button type="button" className="shrink-0 text-xs text-gray-600 hover:underline" onClick={() => setPendingAccount(false)}>
                    Search instead
                  </button>
                </div>
              ) : newAccount ? (
                <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-600">New FeelyFeely account</span>
                    <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => setNewAccount(null)}>
                      Search instead
                    </button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {(
                      [
                        ['firstName', 'First name', 'text'],
                        ['lastName', 'Last name', 'text'],
                        ['email', 'Email', 'email'],
                        ['phone', 'Phone', 'tel'],
                      ] as const
                    ).map(([key, label, type]) => (
                      <Field key={key} label={label}>
                        <input
                          type={type}
                          value={newAccount[key]}
                          onChange={(e) => {
                            const value = e.target.value;
                            setNewAccount((a) => (a ? { ...a, [key]: value } : a));
                            if (key === 'email') setForm((f) => ({ ...f, email: value }));
                          }}
                          onBlur={() =>
                            setForm((f) =>
                              f.name.trim() ? f : { ...f, name: `${newAccount.firstName} ${newAccount.lastName}`.trim() },
                            )
                          }
                          className={inputClass}
                        />
                      </Field>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">They sign in with this account to use My workplace.</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <span className="text-xs font-medium text-gray-600">FeelyFeely account</span>
                  <UserSearchSelect
                    value={accountQuery}
                    onChange={setAccountQuery}
                    onSelect={pickAccount}
                    placeholder="Search by name or email"
                  />
                  {canCreateAccount && (
                    <button
                      type="button"
                      className="text-xs font-medium text-blue-700 hover:underline"
                      onClick={() => setNewAccount(EMPTY_ACCOUNT)}
                    >
                      No account yet? Create one
                    </button>
                  )}
                  <button
                    type="button"
                    className="block text-xs font-medium text-gray-600 hover:underline"
                    onClick={() => setPendingAccount(true)}
                  >
                    They will sign up later: add as pending
                  </button>
                </div>
              ))}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name">
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputClass} />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className={inputClass}
                />
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Title">
                <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputClass} />
              </Field>
              <Field label="Department">
                <select
                  value={form.departmentId}
                  onChange={(e) => setForm((f) => ({ ...f, departmentId: e.target.value }))}
                  className={inputClass}
                >
                  <option value="">None</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Level">
              <select value={form.levelId} onChange={(e) => setForm((f) => ({ ...f, levelId: e.target.value }))} className={inputClass}>
                <option value="">None</option>
                {levelsList
                  .filter((l) => l.status === 'active' || l.id === form.levelId)
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code} · {l.name}
                    </option>
                  ))}
              </select>
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Manager">
                <select value={form.managerId} onChange={(e) => setForm((f) => ({ ...f, managerId: e.target.value }))} className={inputClass}>
                  <option value="">None</option>
                  {employees
                    .filter((e) => e.id !== editing?.id)
                    .map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="Location">
                <LocationSearchInput
                  value={form.location}
                  onChange={(location) => setForm((f) => ({ ...f, location }))}
                  className={inputClass}
                />
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Employment type">
                <select
                  value={form.employmentType}
                  onChange={(e) => setForm((f) => ({ ...f, employmentType: e.target.value as EmploymentType }))}
                  className={inputClass}
                >
                  {Object.entries(EMPLOYMENT_TYPE_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as EmploymentStatus }))}
                  className={inputClass}
                >
                  {Object.entries(EMPLOYMENT_STATUS_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Start date">
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                  className={inputClass}
                />
              </Field>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={upsert.isPending || createAccount.isPending}>
                {upsert.isPending || createAccount.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add employee'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ImportStaffDialog open={importOpen} onOpenChange={setImportOpen} />

      <DeleteConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Remove employee?"
        description={`"${deleting?.name}" will be removed from the directory, with their onboarding and offboarding tasks. To record someone leaving, set their status to Terminated instead.`}
        onConfirm={confirmDelete}
      />
    </HrPageShell>
  );
};

export default DirectoryPage;
